<template>
  <v-dialog
    data-testid="defaultSectionDialog-dialog-defaultSection"
    width="500"
    :value="show"
    @click:outside="$emit('cancel')"
  >
    <v-card data-testid="defaultSectionDialog-card-defaultSection">
      <v-card-title
        data-testid="defaultSectionDialog-cardTitle-defaultSection"
        class="h6 font-weight-bold blue-grey--text text--darken-3 mb-0"
      >
        {{ title }}
      </v-card-title>

      <v-card-subtitle
        class="body-1 blue-grey--text text--darken-2 mt-2 mb-0"
        data-testid="defaultSectionDialog-cardSubtitle-defaultSection"
      >
        {{ subtitle }}

        <div class="mt-2">
          <span class="body-1 blue-grey--text text--darken-2">
            {{ description }}
          </span>
        </div>
      </v-card-subtitle>

      <v-card-text
        class="px-6 pt-2 pb-3"
        data-testid="defaultSectionDialog-cardText-description"
      >
        <v-select
          v-if="showsSectionPicker"
          data-testid="defaultSectionDialog-select-targetSection"
          dense
          hide-details
          item-text="text"
          item-value="value"
          outlined
          :items="availableSections"
          :label="$t('routines.scoring.move_questions_to_section_label')"
          :value="selectedSectionId"
          @input="handleSectionChange"
        />
        <v-form
          v-else-if="requiresSectionName"
          class="mt-4"
          data-testid="defaultSectionDialog-form-defaultSection"
          ref="defaultSectionForm"
        >
          <v-text-field
            data-testid="defaultSectionDialog-textField-sectionName"
            dense
            outlined
            required
            :label="$t('routines.scoring.default_section_field_label')"
            :rules="sectionNameRules"
            :value="sectionName"
            @input="handleSectionNameInput"
          />
        </v-form>
        <div
          v-else
          class="grey lighten-5 rounded-lg pa-3 mt-4"
          data-testid="defaultSectionDialog-card-existingDefaultSection"
        >
          <p
            class="caption font-weight-regular blue-grey--text text--darken-1 mb-1"
          >
            {{ $t('routines.scoring.default_section_existing_label') }}
          </p>
          <p
            class="body-2 font-weight-bold blue-grey--text text--darken-3 mb-0"
          >
            {{ existingSectionName }}
          </p>
        </div>
      </v-card-text>
      <v-card-actions
        class="px-6 pb-5 pt-4 d-flex justify-space-between align-end"
        data-testid="defaultSectionDialog-cardActions-defaultSection"
      >
        <Button
          plain
          color="grey"
          data-testid="defaultSectionDialog-button-cancel"
          :text="leftActionText"
          @click="handleLeftAction"
        />
        <div class="d-flex flex-column justify-end align-end ml-auto">
          <Button
            plain
            color="primary"
            :data-testid="primaryActionTestId"
            :disabled="isPrimaryActionDisabled"
            :text="primaryActionText"
            @click="handlePrimaryAction"
          />
          <Button
            v-if="showsSecondaryAction"
            plain
            color="primary"
            class="mt-2"
            data-testid="defaultSectionDialog-button-createDefaultSection"
            :text="$t('routines.scoring.create_default_section')"
            @click="handleSecondaryAction"
          />
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Button from '@/components/UI/Button'
import { DEFAULT_SECTION_DIALOG_MODES } from './dialogState'

export default {
  name: 'DefaultSectionDialog',

  components: {
    Button,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: DEFAULT_SECTION_DIALOG_MODES.CREATE,
    },
    availableSections: {
      type: Array,
      default: () => [],
    },
    existingSectionName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      mixedModeStep: 'decision',
      selectedSectionId: null,
      sectionName: '',
    }
  },

  computed: {
    dialogCopy() {
      if (this.isMixedModeCreateStep) {
        return {
          title: this.$t(
            'routines.scoring.section_questions_required_warning_title'
          ),
          subtitle: this.$t(
            'routines.scoring.section_questions_required_warning_description'
          ),
          description: '',
        }
      }

      if (this.showsSectionPicker) {
        return {
          title: this.$t(
            'routines.scoring.section_questions_required_warning_title'
          ),
          subtitle: this.$t(
            'routines.scoring.section_questions_required_warning_description'
          ),
          description: this.$t(
            'routines.scoring.orphan_question_recovery_dialog_description'
          ),
        }
      }

      return this.requiresSectionName
        ? {
            title: this.$t(
              'routines.scoring.section_questions_required_warning_title'
            ),
            subtitle: this.$t(
              'routines.scoring.section_questions_required_warning_description'
            ),
            description: this.$t(
              'routines.scoring.create_default_section_dialog_subtitle'
            ),
          }
        : {
            title: this.$t('routines.scoring.move_to_default_dialog_title'),
            subtitle: this.$t(
              'routines.scoring.move_to_default_dialog_subtitle'
            ),
            description: this.$t(
              'routines.scoring.move_to_default_dialog_description'
            ),
          }
    },

    title() {
      return this.dialogCopy.title
    },

    subtitle() {
      return this.dialogCopy.subtitle
    },

    description() {
      return this.dialogCopy.description
    },

    requiresSectionName() {
      return this.mode === DEFAULT_SECTION_DIALOG_MODES.CREATE ||
        this.isMixedModeCreateStep
    },

    showsSectionPicker() {
      return (
        this.mode === DEFAULT_SECTION_DIALOG_MODES.MOVE_TO_SECTION_OR_CREATE &&
        !this.isMixedModeCreateStep
      )
    },

    showsSecondaryAction() {
      return this.showsSectionPicker
    },

    isMixedModeCreateStep() {
      return (
        this.mode === DEFAULT_SECTION_DIALOG_MODES.MOVE_TO_SECTION_OR_CREATE &&
        this.mixedModeStep === 'createDefault'
      )
    },

    leftActionText() {
      return this.isMixedModeCreateStep
        ? this.$t('common.button.back')
        : this.$t('common.button.cancel')
    },

    isPrimaryActionDisabled() {
      if (this.showsSectionPicker) {
        return !this.selectedSectionId
      }

      if (!this.requiresSectionName) {
        return false
      }

      return !this.sectionName?.trim?.()
    },

    primaryActionText() {
      if (this.showsSectionPicker) {
        return this.$t('routines.scoring.move_questions_to_section')
      }

      return this.requiresSectionName
        ? this.$t('routines.scoring.create_default_section')
        : this.$t('routines.scoring.move_to_default_section')
    },

    primaryActionTestId() {
      if (this.showsSectionPicker) {
        return 'defaultSectionDialog-button-moveQuestionsToSection'
      }

      return this.requiresSectionName
        ? 'defaultSectionDialog-button-createDefaultSection'
        : 'defaultSectionDialog-button-moveToDefaultSection'
    },

    sectionNameRules() {
      return [
        value =>
          Boolean(value?.trim?.()) ||
          this.$t('validation.form.this_field_is_required'),
      ]
    },
  },

  watch: {
    show(value) {
      if (!value) {
        this.mixedModeStep = 'decision'
        this.selectedSectionId = null
        this.sectionName = ''
      }
    },
  },

  methods: {
    handleSectionChange(value) {
      this.selectedSectionId = value
    },

    handleSectionNameInput(value) {
      this.sectionName = value
    },

    handlePrimaryAction() {
      if (this.showsSectionPicker) {
        this.$emit('moveToSection', this.selectedSectionId)
        return
      }

      if (!this.requiresSectionName) {
        this.$emit('confirm')
        return
      }

      const isValid = this.$refs.defaultSectionForm?.validate?.() ?? false
      if (!isValid) {
        return
      }

      this.$emit('confirm', this.sectionName.trim())
    },

    handleSecondaryAction() {
      this.mixedModeStep = 'createDefault'
    },

    handleLeftAction() {
      if (this.isMixedModeCreateStep) {
        this.mixedModeStep = 'decision'
        return
      }

      this.$emit('cancel')
    },
  },
}
</script>
